<script setup>
/**
 * Edit Chat Room - AI Focus
 * Página para editar sala de chat existente com wizard
 */
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, maxLength } from '@vuelidate/validators';
import FormInput from 'v3/components/Form/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'shared/components/Spinner.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const roomId = computed(() => Number(route.params.roomId));
const room = computed(() =>
  store.getters['chatRooms/getChatRoom'](roomId.value)
);

const isUpdating = ref(false);
const isLoading = ref(true);
const avatarFile = ref(null);
const avatarPreview = ref(null);
const fileInput = ref(null);

const state = reactive({
  name: '',
  description: '',
});

const rules = {
  name: {
    required,
    minLength: minLength(2),
    maxLength: maxLength(100),
  },
  description: {
    maxLength: maxLength(500),
  },
};

const v$ = useVuelidate(rules, state);

const steps = computed(() => [
  {
    number: 1,
    title: t('CHAT_ROOMS_SETTINGS.EDIT.STEP_1_TITLE'),
    description: t('CHAT_ROOMS_SETTINGS.EDIT.STEP_1_DESC'),
  },
  {
    number: 2,
    title: t('CHAT_ROOMS_SETTINGS.EDIT.STEP_2_TITLE'),
    description: t('CHAT_ROOMS_SETTINGS.EDIT.STEP_2_DESC'),
  },
]);

const currentStep = 1;

onMounted(async () => {
  try {
    await store.dispatch('chatRooms/show', roomId.value);
    if (room.value) {
      state.name = room.value.name || '';
      state.description = room.value.description || '';
      avatarPreview.value = room.value.avatar_url || null;
    }
  } catch {
    useAlert(t('CHAT_ROOMS_SETTINGS.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isLoading.value = false;
  }
});

watch(
  room,
  newRoom => {
    if (newRoom) {
      state.name = newRoom.name || '';
      state.description = newRoom.description || '';
      if (!avatarFile.value) {
        avatarPreview.value = newRoom.avatar_url || null;
      }
    }
  },
  { immediate: true }
);

const triggerFileInput = () => {
  fileInput.value?.click();
};

const handleAvatarSelect = event => {
  const file = event.target.files?.[0];
  if (!file) return;

  avatarFile.value = file;

  const reader = new FileReader();
  reader.onload = e => {
    avatarPreview.value = e.target.result;
  };
  reader.readAsDataURL(file);
};

const updateRoom = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  isUpdating.value = true;
  try {
    await store.dispatch('chatRooms/update', {
      roomId: roomId.value,
      name: state.name,
      description: state.description,
    });

    // Upload avatar if changed
    if (avatarFile.value) {
      await store.dispatch('chatRooms/updateAvatar', {
        roomId: roomId.value,
        file: avatarFile.value,
      });
    }

    useAlert(t('CHAT_ROOMS_SETTINGS.EDIT.API.SUCCESS_MESSAGE'));
    router.push({ name: 'settings_chat_rooms_list' });
  } catch {
    useAlert(t('CHAT_ROOMS_SETTINGS.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isUpdating.value = false;
  }
};

const goToMembers = () => {
  router.push({
    name: 'settings_chat_rooms_edit_members',
    params: { roomId: roomId.value },
  });
};
</script>

<template>
  <div class="flex h-full overflow-auto p-6">
    <div
      v-if="isLoading"
      class="flex items-center justify-center w-full h-full"
    >
      <Spinner />
    </div>
    <div
      v-else
      class="flex w-full max-w-5xl mx-auto border border-n-weak rounded-xl bg-n-solid-2"
    >
      <!-- Left side - Steps -->
      <div class="w-64 flex-shrink-0 p-6 border-r border-n-weak">
        <div class="relative">
          <!-- Connecting line -->
          <div class="absolute left-4 top-8 w-0.5 bg-n-weak step-line" />

          <div class="space-y-6 relative">
            <div
              v-for="step in steps"
              :key="step.number"
              class="flex gap-3 cursor-pointer"
              @click="step.number === 2 ? goToMembers() : null"
            >
              <div
                class="flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium flex-shrink-0 z-10"
                :class="
                  step.number === currentStep
                    ? 'bg-woot-500 text-white'
                    : 'bg-n-solid-3 border border-n-weak text-n-slate-11'
                "
              >
                {{ step.number }}
              </div>
              <div class="flex-1 min-w-0">
                <p
                  class="text-sm font-medium"
                  :class="
                    step.number === currentStep
                      ? 'text-woot-500'
                      : 'text-n-slate-11'
                  "
                >
                  {{ step.title }}
                </p>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  {{ step.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right side - Form -->
      <div class="flex-1 p-6 overflow-auto">
        <div class="max-w-md">
          <h2 class="text-xl font-semibold text-n-slate-12 mb-1">
            {{ t('CHAT_ROOMS_SETTINGS.EDIT.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11 mb-6">
            {{ t('CHAT_ROOMS_SETTINGS.EDIT.DESC') }}
          </p>

          <!-- Avatar upload -->
          <div class="mb-6">
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              {{ t('CHAT_ROOMS_SETTINGS.FORM.AVATAR.LABEL') }}
            </label>
            <div class="flex items-center gap-4">
              <div class="cursor-pointer" @click="triggerFileInput">
                <Avatar
                  :src="avatarPreview"
                  :name="state.name || 'S'"
                  :size="56"
                  rounded-full
                />
              </div>
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                class="hidden"
                @change="handleAvatarSelect"
              />
              <p
                class="text-sm text-n-slate-11 cursor-pointer hover:text-n-slate-12"
                @click="triggerFileInput"
              >
                {{ t('CHAT_ROOMS_SETTINGS.FORM.AVATAR.UPLOAD') }}
              </p>
            </div>
          </div>

          <!-- Name input -->
          <div class="mb-4">
            <FormInput
              v-model="state.name"
              name="name"
              spacing="compact"
              :label="$t('CHAT_ROOMS_SETTINGS.FORM.NAME.LABEL')"
              :placeholder="t('CHAT_ROOMS_SETTINGS.FORM.NAME.PLACEHOLDER')"
              :has-error="v$.name.$error"
              :error-message="
                v$.name.$error ? $t('CHAT_ROOMS_SETTINGS.FORM.NAME.ERROR') : ''
              "
              @blur="v$.name.$touch"
            />
          </div>

          <!-- Description input -->
          <div class="mb-6">
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('CHAT_ROOMS_SETTINGS.FORM.DESCRIPTION.LABEL') }}
            </label>
            <textarea
              v-model="state.description"
              :placeholder="
                t('CHAT_ROOMS_SETTINGS.FORM.DESCRIPTION.PLACEHOLDER')
              "
              rows="3"
              class="w-full px-3 py-2 text-sm bg-n-alpha-1 border border-n-weak rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent text-n-slate-12 placeholder:text-n-slate-9"
              @blur="v$.description.$touch"
            />
          </div>

          <!-- Action buttons -->
          <div class="flex items-center gap-3">
            <Button
              :label="$t('CHAT_ROOMS_SETTINGS.FORM.UPDATE')"
              :disabled="v$.name.$invalid || isUpdating"
              :is-loading="isUpdating"
              @click="updateRoom"
            />
            <Button
              :label="t('CHAT_ROOMS_SETTINGS.FORM.NEXT')"
              icon="i-lucide-arrow-right"
              icon-position="right"
              slate
              faded
              @click="goToMembers"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.step-line {
  height: calc(100% - 2rem);
}
</style>
